// =============================================================================
// FILE: packages/renderer/src/components/submittal/SubmittalGenerator.tsx
// PURPOSE: "GENERATE COMPLETE SUBMITTAL SET" — the one-click, camera-ready
//   submittal experience. A prominent gold-bordered action (Global Master),
//   a cinematic generation workflow, and a live fly-out preview of page 1
//   with an R² "Download Ready" confirmation.
// =============================================================================

import { useCallback, useRef, useState, type JSX } from 'react';
import {
  FileStack, Loader2, CheckCircle2, Download, X, Sparkles, ShieldCheck,
} from 'lucide-react';
import { useDashboardStats } from '../../hooks/useDashboard.js';
import { useLicense } from '../../hooks/useLicense.js';
import { useAuthStore } from '../../stores/auth.store.js';
import { getBrand } from '@bim/shared/config/brand.config.js';
import { growthAgent } from '../../stores/growthAgent.store.js';
import { isDemoMode, SIERRA_MANOR_PROJECT_NAME } from '../../config/demoMode.js';
import { generateSubmittalSet, type SubmittalResult, type DocumentStyle, type SheetStatus } from '../../lib/submittalGenerator.js';

type Phase = 'idle' | 'working' | 'ready';

interface Stage { label: string; detail: string }

const STAGES: Stage[] = [
  { label: 'Authenticating RCDD credentials',  detail: 'Verifying licensed engineer of record' },
  { label: 'Fetching site metadata',           detail: 'Reading offline-first SQLite project graph' },
  { label: 'Compiling floor plans',            detail: 'Resolving buildings, spaces & telecom rooms' },
  { label: 'Rendering system overview',        detail: 'Vectorising live project topology' },
  { label: 'Building rack elevations',         detail: 'Placing switches, panels & power' },
  { label: 'Validating RCDD standards',        detail: 'Cross-checking ANSI/TIA & BICSI conformance' },
  { label: 'Applying RCD² certification stamp', detail: 'Sealing the submittal set' },
  { label: 'Finalizing submittal set',         detail: 'Assembling print-ready PDF' },
];

export function SubmittalGenerator(): JSX.Element {
  const stats = useDashboardStats();
  const { licenseStatus } = useLicense();
  const user  = useAuthStore((s) => s.user);
  const brand = getBrand();

  const [phase, setPhase]       = useState<Phase>('idle');
  const [stageIdx, setStageIdx] = useState(0);
  const [preview, setPreview]   = useState<string | null>(null);
  const [result, setResult]     = useState<SubmittalResult | null>(null);
  const runningRef = useRef(false);

  const [showDetails, setShowDetails] = useState(false);
  const [details, setDetails] = useState({
    projectNumber: '', drawingTitle: 'Telecommunications Infrastructure — Submittal Set',
    drawingNumber: 'T-001', discipline: 'Telecommunications (ICT)', scale: 'NTS',
    status: 'Design Development' as SheetStatus, checkedBy: '', approvedBy: '',
    documentStyle: 'corporate' as DocumentStyle,
  });
  const setD = (k: keyof typeof details) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setDetails((p) => ({ ...p, [k]: e.target.value }));

  const isDesigner = licenseStatus?.tier === 'designer';
  const accent     = isDesigner ? '#fcd34d' : '#25B5A8';
  const tierLabel  = isDesigner ? 'GLOBAL MASTER'
    : licenseStatus?.tier === 'contractor' ? 'CONTRACTOR'
    : licenseStatus?.tier === 'technician' ? 'TECHNICIAN' : 'LICENSED';

  const authorName = (() => {
    const name = user
      ? ([user.firstName, user.lastName].filter(Boolean).join(' ') || user.email?.split('@')[0] || 'Engineer')
      : 'Engineer';
    if (licenseStatus?.bicsiId && licenseStatus.bicsiId !== 'dev-preview') {
      const id = licenseStatus.bicsiId === 'MASTER-ADMIN' ? 'MASTER' : licenseStatus.bicsiId.replace(/^#/, '');
      return `${name}, RCDD #${id}`;
    }
    return name;
  })();

  const run = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    setPhase('working');
    setStageIdx(0);
    setPreview(null);
    setResult(null);

    const minPerStage = 520; // ms — keeps the sequence cinematic
    const startedAt = Date.now();

    // Kick off the real generation in parallel with the staged animation.
    const genPromise = generateSubmittalSet(
      stats.data,
      {
        author:      authorName,
        productName: brand.productName,
        tierLabel,
        tierColor:   accent,
        projectName: isDemoMode() ? SIERRA_MANOR_PROJECT_NAME : 'Active Portfolio',
        projectNumber: details.projectNumber || undefined,
        drawingTitle: details.drawingTitle || undefined,
        drawingNumber: details.drawingNumber || undefined,
        discipline: details.discipline || undefined,
        scale: details.scale || undefined,
        status: details.status,
        designedBy: authorName,
        checkedBy: details.checkedBy || undefined,
        approvedBy: details.approvedBy || undefined,
        documentStyle: details.documentStyle,
      },
      (dataUrl, i) => { if (i === 0) setPreview(dataUrl); },
    );

    for (let i = 0; i < STAGES.length; i++) {
      setStageIdx(i);
      await new Promise((r) => setTimeout(r, minPerStage));
    }

    const res = await genPromise;
    // Ensure the whole thing never feels instant (min ~4s of theatre)
    const elapsed = Date.now() - startedAt;
    if (elapsed < 4200) await new Promise((r) => setTimeout(r, 4200 - elapsed));

    setResult(res);
    setPreview(res.firstPageDataUrl);
    setPhase('ready');
    growthAgent.celebrate('generic_win');
    runningRef.current = false;
  }, [stats.data, authorName, brand.productName, tierLabel, accent, details]);

  const close = useCallback(() => {
    if (result) URL.revokeObjectURL(result.blobUrl);
    setPhase('idle');
    setResult(null);
    setPreview(null);
  }, [result]);

  const download = useCallback(() => {
    if (!result) return;
    const a = document.createElement('a');
    a.href = result.blobUrl;
    a.download = result.fileName;
    a.click();
  }, [result]);

  const progressPct = phase === 'ready' ? 100 : Math.round(((stageIdx + 1) / STAGES.length) * 100);

  return (
    <>
      <style>{`
        @keyframes sg-sheen { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        @keyframes sg-flyin { 0%{opacity:0; transform:translateY(28px) scale(0.94)} 100%{opacity:1; transform:translateY(0) scale(1)} }
        @keyframes sg-scan  { 0%{transform:translateY(0)} 100%{transform:translateY(340px)} }
        @keyframes sg-pop   { 0%{transform:scale(0.6); opacity:0} 60%{transform:scale(1.12)} 100%{transform:scale(1); opacity:1} }
      `}</style>

      {/* ── Sheet Details form (title-block metadata + document style) ── */}
      <div data-testid="sheet-details" className="rounded-xl mb-3 overflow-hidden" style={{ border: '1px solid rgba(148,163,184,0.2)', background: 'rgba(15,23,42,0.4)' }}>
        <button type="button" data-testid="sheet-details-toggle" onClick={() => setShowDetails((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-2.5 text-left">
          <span className="text-[12px] font-semibold text-zinc-200">Sheet Details <span className="text-zinc-500 font-normal">· title block & document style</span></span>
          <span className="text-[11px]" style={{ color: accent }}>{showDetails ? 'Hide' : 'Edit'}</span>
        </button>
        {showDetails && (
          <div className="px-4 pb-4 grid grid-cols-2 gap-2.5" data-testid="sheet-details-form">
            {([
              ['projectNumber', 'Project No.', 'RCD2-2026-001'],
              ['drawingNumber', 'Drawing No.', 'T-001'],
              ['drawingTitle', 'Drawing Title', 'Telecommunications Infrastructure'],
              ['discipline', 'Discipline', 'Telecommunications (ICT)'],
              ['scale', 'Scale', 'NTS'],
              ['checkedBy', 'Checked By', 'Name'],
              ['approvedBy', 'Approved By', 'Name'],
            ] as const).map(([k, label, ph]) => (
              <label key={k} className="text-[10px] uppercase tracking-wide text-zinc-500">
                {label}
                <input data-testid={`sd-${k}`} value={details[k]} onChange={setD(k)} placeholder={ph}
                  className="mt-1 w-full px-2.5 py-1.5 rounded-md text-[12px] text-zinc-100 outline-none"
                  style={{ background: 'rgba(2,6,12,0.6)', border: '1px solid rgba(148,163,184,0.25)' }} />
              </label>
            ))}
            <label className="text-[10px] uppercase tracking-wide text-zinc-500">
              Status
              <select data-testid="sd-status" value={details.status} onChange={setD('status')}
                className="mt-1 w-full px-2.5 py-1.5 rounded-md text-[12px] text-zinc-100 outline-none"
                style={{ background: 'rgba(2,6,12,0.6)', border: '1px solid rgba(148,163,184,0.25)' }}>
                {['Preliminary', 'Design Development', 'Construction Issue', 'Record Drawing'].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="text-[10px] uppercase tracking-wide text-zinc-500 col-span-2">
              Document Appearance
              <select data-testid="sd-documentStyle" value={details.documentStyle} onChange={setD('documentStyle')}
                className="mt-1 w-full px-2.5 py-1.5 rounded-md text-[12px] text-zinc-100 outline-none"
                style={{ background: 'rgba(2,6,12,0.6)', border: '1px solid rgba(148,163,184,0.25)' }}>
                <option value="corporate">Corporate Professional — clean white, minimal branding</option>
                <option value="rcd2-branded">RCD² Branded — visible branding</option>
                <option value="client-standard">Client Standard — reduced branding</option>
              </select>
            </label>
          </div>
        )}
      </div>

      {/* ── The prominent action ── */}
      <button
        type="button"
        data-testid="generate-submittal-btn"
        onClick={run}
        className="group relative w-full overflow-hidden rounded-xl px-6 py-5 text-left transition-transform"
        style={{
          background: isDesigner
            ? 'linear-gradient(135deg, rgba(252,211,77,0.14), rgba(180,120,20,0.08))'
            : 'linear-gradient(135deg, rgba(37,181,168,0.14), rgba(20,90,90,0.08))',
          border: `1.5px solid ${accent}`,
          boxShadow: `0 0 0 1px ${accent}22, 0 10px 40px ${accent}22, inset 0 0 40px ${accent}0f`,
        }}
        onMouseOver={(e) => { (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0 0 1px ${accent}44, 0 14px 60px ${accent}44, inset 0 0 60px ${accent}18`; }}
        onMouseOut={(e) =>  { (e.currentTarget as HTMLButtonElement).style.boxShadow = `0 0 0 1px ${accent}22, 0 10px 40px ${accent}22, inset 0 0 40px ${accent}0f`; }}
      >
        {/* moving sheen */}
        <span aria-hidden style={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(100deg, transparent 30%, ${accent}22 50%, transparent 70%)`,
          backgroundSize: '200% 100%',
          animation: 'sg-sheen 3.4s linear infinite',
          pointerEvents: 'none',
        }} />
        <div className="relative flex items-center gap-4">
          <div className="grid place-items-center rounded-lg shrink-0"
            style={{ width: 52, height: 52, background: `${accent}1f`, border: `1px solid ${accent}55` }}>
            <FileStack className="h-6 w-6" style={{ color: accent }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: `${accent}cc` }}>
              {tierLabel} · One-Click Deliverable
            </div>
            <div className="text-lg font-extrabold tracking-tight text-zinc-50 leading-tight mt-0.5">
              GENERATE COMPLETE SUBMITTAL SET
            </div>
            <div className="text-[12px] text-zinc-400 mt-0.5">
              Branded cover · System Overview · Certified rack elevation — sealed as a print-ready PDF
            </div>
          </div>
          <Sparkles className="h-5 w-5 shrink-0" style={{ color: accent }} />
        </div>
      </button>

      {/* ── Workflow overlay ── */}
      {phase !== 'idle' && (
        <div
          data-testid="submittal-overlay"
          className="fixed inset-0 z-[10000] grid place-items-center p-6"
          style={{ background: 'rgba(4,7,14,0.82)', backdropFilter: 'blur(6px)' }}
        >
          <div
            className="relative w-full max-w-4xl rounded-2xl overflow-hidden"
            style={{
              background: 'rgba(9,14,24,0.98)',
              border: `1.5px solid ${accent}55`,
              boxShadow: `0 30px 120px rgba(0,0,0,0.7), 0 0 60px ${accent}22`,
            }}
          >
            {/* header */}
            <div className="flex items-center justify-between px-6 py-4"
              style={{ borderBottom: `1px solid ${accent}22` }}>
              <div className="flex items-center gap-3">
                <ShieldCheck className="h-5 w-5" style={{ color: accent }} />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: `${accent}cc` }}>
                    RCD² Submittal Engine
                  </div>
                  <div className="text-sm font-bold text-zinc-100">
                    {phase === 'ready' ? 'Submittal Set Ready' : 'Generating Submittal Set'}
                  </div>
                </div>
              </div>
              <button
                type="button"
                data-testid="submittal-close-btn"
                onClick={close}
                className="h-8 w-8 grid place-items-center rounded-md text-zinc-500 hover:text-zinc-200 transition"
                style={{ border: '1px solid rgba(63,63,70,0.6)' }}
                disabled={phase === 'working'}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              {/* left — workflow / status */}
              <div className="p-6">
                {/* progress bar */}
                <div className="mb-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Progress</span>
                    <span className="text-[12px] font-mono font-bold" style={{ color: accent }} data-testid="submittal-progress-pct">
                      {progressPct}%
                    </span>
                  </div>
                  <div className="h-2 w-full rounded-full overflow-hidden" style={{ background: 'rgba(63,63,70,0.5)' }}>
                    <div style={{
                      width: `${progressPct}%`, height: '100%',
                      background: `linear-gradient(90deg, ${accent}, ${accent}aa)`,
                      boxShadow: `0 0 12px ${accent}88`,
                      transition: 'width 0.45s cubic-bezier(0.4,0,0.2,1)',
                    }} />
                  </div>
                </div>

                {/* stage list */}
                <ul className="space-y-2.5" data-testid="submittal-stage-list">
                  {STAGES.map((s, i) => {
                    const done   = phase === 'ready' || i < stageIdx;
                    const active = phase === 'working' && i === stageIdx;
                    return (
                      <li key={s.label} className="flex items-start gap-3">
                        <span className="mt-0.5 shrink-0">
                          {done ? (
                            <CheckCircle2 className="h-4 w-4" style={{ color: accent }} />
                          ) : active ? (
                            <Loader2 className="h-4 w-4 animate-spin" style={{ color: accent }} />
                          ) : (
                            <span className="block h-4 w-4 rounded-full border" style={{ borderColor: 'rgba(100,116,139,0.5)' }} />
                          )}
                        </span>
                        <div className="min-w-0">
                          <div className="text-[13px] font-medium"
                            style={{ color: done || active ? 'rgb(226,232,240)' : 'rgba(100,116,139,0.7)' }}>
                            {s.label}
                          </div>
                          {active && (
                            <div className="text-[11px] text-zinc-500">{s.detail}</div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* right — live preview / fly-out */}
              <div className="p-6 flex flex-col items-center justify-center"
                style={{ background: 'rgba(6,10,18,0.6)', borderLeft: `1px solid ${accent}18` }}>
                <div className="relative rounded-lg overflow-hidden w-full"
                  style={{ aspectRatio: '1754 / 1240', border: `1px solid ${accent}33`, background: '#ffffff' }}>
                  {preview ? (
                    <img
                      src={preview}
                      alt="Submittal cover preview"
                      data-testid="submittal-preview-img"
                      className="w-full h-full object-cover"
                      style={{ animation: 'sg-flyin 0.5s cubic-bezier(0.2,0.8,0.2,1)' }}
                    />
                  ) : (
                    <div className="absolute inset-0 grid place-items-center text-zinc-600 text-[11px] uppercase tracking-widest">
                      Compiling page 1…
                    </div>
                  )}
                  {/* scan line while working */}
                  {phase === 'working' && (
                    <div aria-hidden style={{
                      position: 'absolute', left: 0, right: 0, top: 0, height: 2,
                      background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
                      boxShadow: `0 0 18px ${accent}`,
                      animation: 'sg-scan 1.6s ease-in-out infinite alternate',
                    }} />
                  )}
                </div>

                {phase === 'ready' ? (
                  <div className="w-full mt-5" style={{ animation: 'sg-pop 0.5s ease-out' }}>
                    <div className="flex items-start gap-3 rounded-lg p-3 mb-4"
                      style={{ background: `${accent}12`, border: `1px solid ${accent}33` }}>
                      <Sparkles className="h-4 w-4 mt-0.5 shrink-0" style={{ color: accent }} />
                      <p className="text-[12px] text-zinc-300 leading-relaxed m-0" data-testid="submittal-ready-msg">
                        <span className="font-semibold" style={{ color: accent }}>R²:</span>{' '}
                        Your certified submittal set is sealed and download-ready — 3 pages,
                        stamped and standards-checked. Impressive work.
                      </p>
                    </div>
                    <button
                      type="button"
                      data-testid="submittal-download-btn"
                      onClick={download}
                      className="w-full flex items-center justify-center gap-2 rounded-lg px-5 py-3 font-bold text-[13px] uppercase tracking-[0.14em] transition-transform hover:scale-[1.02]"
                      style={{ background: accent, color: '#0a0f1a', boxShadow: `0 8px 30px ${accent}55` }}
                    >
                      <Download className="h-4 w-4" />
                      Download PDF
                    </button>
                  </div>
                ) : (
                  <div className="mt-5 text-[11px] uppercase tracking-[0.18em] text-zinc-500 text-center">
                    Live preview · page 1 of 3
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
